<template>
  <el-dialog
    :visible.sync="visibleSync"
    :show-close="true"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :before-close="handleClose"
    width="420px"
    custom-class="rate-progress-dialog"
    append-to-body
  >
    <div class="rp-wrap">
      <div class="rp-title">{{ $t('order.rateProgress.title') }}</div>
      <div class="rp-sub">{{ $t('order.rateProgress.subtitle') }}</div>

      <div class="rp-bar">
        <div class="rp-bar-fill" :style="{ width: percent + '%' }"></div>
      </div>
      <div class="rp-meta">
        <span v-if="total > 0">{{ $t('order.rateProgress.completed', { done: doneCount, total: total }) }}</span>
        <span v-else>{{ $t('order.rateProgress.statusLoading') }}...</span>
        <span>{{ percent }}%</span>
      </div>

      <div class="rp-channels" v-if="channels.length">
        <div v-for="(ch, i) in channels" :key="i" class="rp-ch-item">
          <span class="rp-ch-name">{{ ch.name }}</span>
          <span class="rp-ch-status" :class="ch.status">
            <span class="rp-dot"></span>
            <template v-if="ch.status === 'pending'">{{ $t('order.rateProgress.statusPending') }}</template>
            <template v-else-if="ch.status === 'loading'">{{ $t('order.rateProgress.statusLoading') }}</template>
            <template v-else-if="ch.status === 'done'">{{ $t('order.rateProgress.statusDone') }}</template>
            <template v-else-if="ch.status === 'fail'">{{ $t('order.rateProgress.statusFail') }}</template>
          </span>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { RATES_STREAM_URL, RATES_NEW_STREAM_URL } from "@/api/order";

export default {
  name: "RateProgress",
  props: {
    visible: { type: Boolean, default: false },
    streamType: { type: String, default: 'domestic' } // 'domestic' or 'international'
  },
  data() {
    return {
      channels: [],
      percent: 0,
      doneCount: 0,
      total: 0,
      abortController: null,
      resultData: null
    };
  },
  computed: {
    visibleSync: {
      get() { return this.visible; },
      set(val) { this.$emit("update:visible", val); }
    }
  },
  methods: {
    // 由父组件调用，传入请求参数，开始流式测价
    async startStream(formData) {
      this.channels = [];
      this.percent = 0;
      this.doneCount = 0;
      this.total = 0;
      this.resultData = null;

      this.abortController = new AbortController();

      try {
        const url = this.streamType === 'international' ? RATES_NEW_STREAM_URL : RATES_STREAM_URL;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json;charset=UTF-8' },
          body: JSON.stringify(formData),
          signal: this.abortController.signal
        });

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // 保留未完成的行

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const jsonStr = line.slice(6);
              try {
                const event = JSON.parse(jsonStr);
                this.handleEvent(event);
              } catch (e) { /* 忽略解析错误 */ }
            }
          }
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          this.$emit('stream-error', e);
        }
      }
    },

    handleEvent(event) {
      switch (event.type) {
        case 'init':
          // 收到渠道列表，初始化
          this.total = event.total;
          this.channels = event.channels.map(name => ({
            name,
            status: 'pending'
          }));
          break;

        case 'done':
          // 某个渠道测价成功
          this.$set(this.channels, event.index, {
            ...this.channels[event.index],
            status: 'done'
          });
          this.doneCount = event.done;
          this.percent = Math.round((event.done / event.total) * 100);
          break;

        case 'fail':
          // 某个渠道测价失败
          this.$set(this.channels, event.index, {
            ...this.channels[event.index],
            status: 'fail'
          });
          this.doneCount = event.done;
          this.percent = Math.round((event.done / event.total) * 100);
          break;

        case 'skip':
          // 被跳过表示当前条件下没有可用报价，按失败展示，避免误导为测价成功
          this.$set(this.channels, event.index, {
            ...this.channels[event.index],
            status: 'fail'
          });
          this.doneCount = event.done;
          this.percent = Math.round((event.done / event.total) * 100);
          break;

        case 'complete':
          // 全部完成
          this.percent = 100;
          this.resultData = event;
          this.$emit('stream-complete', event);
          // 延迟关闭
          setTimeout(() => {
            this.visibleSync = false;
          }, 500);
          break;

        case 'error':
          this.$emit('stream-error', { message: event.msg });
          setTimeout(() => {
            this.visibleSync = false;
          }, 300);
          break;
      }
    },

    abort() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    },

    // 用户点右上角叉号 / Element 默认关闭流程触发：先终止 SSE，通知父组件，再真正关闭
    handleClose(done) {
      this.abort();
      this.$emit('stream-abort');
      done();
    }
  },
  beforeDestroy() {
    this.abort();
  }
};
</script>

<style scoped>
.rp-wrap { padding: 4px 2px; }
.rp-title { font-size: 16px; font-weight: 600; color: #303133; margin-bottom: 4px; }
.rp-sub { font-size: 12px; color: #909399; margin-bottom: 16px; }
.rp-bar { height: 8px; background: #f0f2f5; border-radius: 4px; overflow: hidden; }
.rp-bar-fill { height: 100%; background: linear-gradient(90deg, #67C23A, #409EFF); border-radius: 4px; transition: width 0.3s ease; }
.rp-meta { display: flex; justify-content: space-between; margin-top: 8px; font-size: 12px; color: #606266; }
.rp-channels { margin-top: 16px; border-top: 1px solid #f0f2f5; padding-top: 14px; }
.rp-ch-item { display: flex; align-items: center; justify-content: space-between; padding: 5px 0; font-size: 13px; white-space: nowrap; }
.rp-ch-name { color: #303133; }
.rp-ch-status { font-size: 12px; display: inline-flex; align-items: center; gap: 6px; }
.rp-ch-status.pending { color: #909399; }
.rp-ch-status.loading { color: #409EFF; }
.rp-ch-status.done { color: #67C23A; }
.rp-ch-status.fail { color: #F56C6C; }
.rp-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.rp-ch-status.loading .rp-dot { animation: rp-pulse 1s infinite; }
@keyframes rp-pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.6); opacity: 0.5; } }
</style>

<style>
/* header 内只保留关闭叉号（headerbtn），通过 padding: 0 让 header 不占高度 */
.rate-progress-dialog .el-dialog__header { padding: 0; }
.rate-progress-dialog .el-dialog__headerbtn { top: 14px; right: 14px; z-index: 10; }
.rate-progress-dialog .el-dialog__body { padding: 24px 26px; }
.rate-progress-dialog { min-width: 420px; }
</style>
